// One purchase request (M6): what was asked for, from whom, why (the restock suggestion's
// reasoning when it came from there), and what the manager decided product by product in
// the approvals inbox. Once decided it leads with what was really ordered (the order's value)
// and keeps what was asked for as the second figure (UI6A-1): a manager never reads the
// asked amount as the spend. A decided request says who decided, when and why, right there
// (UI6A-107), from its approval in the inbox.

import type { PurchaseRequest } from '@enp/domain/api';
import { Badge, Button, cn, Money, PageHeader, Skeleton } from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, Check, ClipboardCheck, History, X } from 'lucide-react';
import { meQueryOptions } from '../../../../auth/session.js';
import {
  lineOutcome,
  requestMoney,
  sinceAskedText,
  unpricedText,
} from '../../../../purchasing/figures.js';
import { OverlapList, useRequestOverlaps } from '../../../../purchasing/inbox.js';
import {
  BaseQty,
  PurchasingBreadcrumb,
  RequestStatusBadge,
  Section,
  useCan,
} from '../../../../purchasing/parts.js';
import { requestQuery, restockQuery } from '../../../../purchasing/queries.js';
import { RestockExplanation, suggestionOf } from '../../../../purchasing/restock-parts.js';
import { packsText, REQUEST_ORIGIN } from '../../../../purchasing/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { plainDateLabel, accountToday } from '../../../../stock/labels.js';
import { Fact, Facts } from '../../../../stock-control/parts.js';
import { approvalQuery } from '../../../../stock-control/queries.js';

export const Route = createFileRoute('/_app/purchasing/requests/$requestId')({
  component: RequestPage,
});

/** A line's money and packs: once decided, what was ordered first, what was asked after. */
function LineFigures({ line }: { line: PurchaseRequest['lines'][number] }) {
  const o = lineOutcome(line);
  const uom = line.supplier_item.pack_uom;
  const asked = (
    <>
      {packsText(o.askedPacks, uom)} ·{' '}
      {o.askedCents === null ? 'no price' : <Money cents={o.askedCents} />}
    </>
  );
  if (o.decision === 'waiting') {
    return (
      <span className="flex flex-col items-end leading-tight" data-testid="line-figures">
        <span className="font-semibold text-fg">
          {o.askedCents === null ? (
            <span className="text-sm font-normal text-muted">No price</span>
          ) : (
            <Money cents={o.askedCents} />
          )}
        </span>
        <span className="text-sm text-muted">Asked {packsText(o.askedPacks, uom)}</span>
      </span>
    );
  }
  if (o.decision === 'left_out') {
    return (
      <span className="flex flex-col items-end gap-1 leading-tight" data-testid="line-figures">
        <Badge tone="danger">
          <X aria-hidden />
          Not ordered
        </Badge>
        <span className="text-sm text-muted">
          Asked <span className="line-through">{asked}</span>
        </span>
      </span>
    );
  }
  return (
    <span className="flex flex-col items-end gap-1 leading-tight" data-testid="line-figures">
      <span className="flex items-center gap-2">
        <Badge tone="success">
          <Check aria-hidden />
          Ordered
        </Badge>
        <span className="font-semibold text-fg">
          {packsText(o.orderedPacks as string, uom)} ·{' '}
          {o.orderedCents === null ? 'no price' : <Money cents={o.orderedCents} />}
        </span>
      </span>
      {o.changed && (
        <span className="text-sm text-muted">
          Asked <span className="line-through">{asked}</span>
        </span>
      )}
    </span>
  );
}

/** The request's money as a fact: ordered first once decided, asked as the second figure. */
function MoneyFact({ r }: { r: PurchaseRequest }) {
  const m = requestMoney(r);
  const asked =
    m.asked === null ? (
      'no prices known'
    ) : (
      <>
        <Money cents={m.asked} /> ex VAT{unpricedText(m.askedUnpriced)}
      </>
    );
  if (m.kind === 'waiting') {
    return (
      <Fact label="Asked for">
        {m.asked === null ? (
          'No prices known'
        ) : (
          <>
            <Money cents={m.asked} className="font-semibold" /> ex VAT
            {m.askedUnpriced > 0 &&
              `, without ${m.askedUnpriced} unpriced ${m.askedUnpriced === 1 ? 'product' : 'products'}`}
          </>
        )}
      </Fact>
    );
  }
  if (m.kind === 'nothing') {
    return (
      <Fact label="Ordered">
        <span className="font-semibold">Nothing</span>{' '}
        <span className="text-muted">(asked {asked})</span>
      </Fact>
    );
  }
  return (
    <Fact label={m.cancelled ? 'Ordered, then cancelled' : 'Ordered'}>
      <span data-testid="request-ordered">
        <Money
          cents={m.ordered}
          className={cn('font-semibold', m.cancelled && 'text-muted line-through')}
        />{' '}
        ex VAT{unpricedText(m.orderedUnpriced)} on {m.orderNumber}
        {m.cancelled && <span className="font-medium"> · cancelled: nothing owed</span>}
        {m.differs && <span className="text-muted"> (asked {asked})</span>}
      </span>
    </Fact>
  );
}

function RequestPage() {
  const { requestId } = Route.useParams();
  const q = useQuery(requestQuery(requestId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canApprove = useCan('purchasing.approve');
  const r = q.data;
  const approval = useQuery({
    ...approvalQuery(r?.approval_request_id ?? ''),
    enabled: Boolean(r && r.status !== 'requested' && r.approval_request_id),
  });
  // What else already covers its products, as in the inbox (UI6A-101, loop 3).
  const overlaps = useRequestOverlaps(r);
  // Today's suggestion, beside the one kept when it was asked (UI8C-1); the same read as above.
  const live = useQuery({
    ...restockQuery({ branch: r?.branch.id }),
    enabled: r?.status === 'requested',
  });
  useDocumentTitle(r ? r.number : 'Purchase request');

  if (q.isError && !r) {
    return (
      <>
        <PurchasingBreadcrumb
          trail={[{ to: '/purchasing/requests', label: 'Requests' }]}
          page="Request"
        />
        <div className="mt-4 max-w-2xl">
          <ErrorPanel
            error={q.error}
            titleAs="h1"
            what="This request"
            onRetry={() => void q.refetch()}
            extraAction={
              <Button variant="secondary" asChild>
                <Link to="/purchasing/requests">Back to requests</Link>
              </Button>
            }
          />
        </div>
      </>
    );
  }
  if (!r) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <h1 className="sr-only">Purchase request</h1>
        <span role="status" className="sr-only">
          Loading the request…
        </span>
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  const po = r.purchase_order;
  const decider = approval.data?.decided_by?.name ?? null;
  const reason = approval.data?.reason ?? null;
  /** "Rejected by Owner, 24 Sep, 12:00: “Plenty of boxes left”." */
  const decision = (verb: string) => (
    <span data-testid="decision-inline">
      {verb}
      {decider ? ` by ${decider}` : ''}
      {r.decided_at && (
        <>
          , <AccountDate value={r.decided_at} withTime />
        </>
      )}
      {reason ? (
        <>
          : <q className="font-medium">{reason}</q>.
        </>
      ) : (
        '.'
      )}{' '}
    </span>
  );

  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[{ to: '/purchasing/requests', label: 'Requests' }]}
            page={r.number}
          />
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>
              Purchase request <span className="whitespace-nowrap font-mono">{r.number}</span>
            </span>
            <span className="font-body">
              <RequestStatusBadge status={r.status} />
            </span>
          </span>
        }
        description={`${r.supplier.name} · ${r.branch.name} · ${REQUEST_ORIGIN[r.origin]}`}
      />
      <div className="flex flex-col gap-5">
        {r.status === 'requested' && (
          <Notice tone="warning" title="Waiting for a decision">
            It waits in the approvals inbox. A manager approves or rejects each product; approving
            makes the purchase order from what was approved.
            {r.approval_request_id && (
              <span className="mt-2 block">
                <Button size="sm" variant={canApprove ? 'primary' : 'secondary'} asChild>
                  <Link to="/approvals" search={{ id: r.approval_request_id }}>
                    <ClipboardCheck aria-hidden />
                    {canApprove
                      ? 'Decide it in the approvals inbox'
                      : 'See it in the approvals inbox'}
                  </Link>
                </Button>
              </span>
            )}
          </Notice>
        )}
        {po && (
          <Notice
            tone={po.status === 'cancelled' ? 'danger' : 'neutral'}
            title={
              po.status === 'cancelled'
                ? `Approved, but order ${po.number} was cancelled`
                : `Approved: order ${po.number}`
            }
          >
            {decision('Approved')}
            {po.status === 'cancelled' && 'Nothing is owed on it. '}
            <Link
              to="/purchasing/orders/$orderId"
              params={{ orderId: po.id }}
              className="inline-flex items-center gap-1 rounded-sm font-medium text-primary-text underline underline-offset-2"
            >
              Open order {po.number}
              <ArrowRight aria-hidden className="size-4" />
            </Link>
          </Notice>
        )}
        {(r.status === 'rejected' || r.status === 'cancelled') && (
          <Notice
            tone="neutral"
            title={
              r.status === 'rejected' ? 'Rejected: nothing ordered' : 'Closed without a decision'
            }
          >
            {decision(r.status === 'rejected' ? 'Rejected' : 'Closed')}
            {r.approval_request_id && (
              <Link
                to="/approvals"
                search={{ id: r.approval_request_id, view: r.status }}
                className="rounded-sm font-medium text-primary-text underline underline-offset-2"
              >
                See it in the approvals inbox
              </Link>
            )}
          </Notice>
        )}

        <Section id="request-facts" title="Request">
          <Facts>
            <MoneyFact r={r} />
            <Fact label="Asked by">
              {r.requested_by?.name ?? 'Unknown'}, <AccountDate value={r.requested_at} withTime />
            </Fact>
            <Fact label="Needed by">
              {r.needed_by ? plainDateLabel(r.needed_by, today) : 'No date given'}
            </Fact>
            <Fact label="Note">{r.note ?? 'None'}</Fact>
          </Facts>
          {po && (
            <p className="text-sm text-muted">
              Line amounts are at the prices when it was asked for; the order’s own total is the one
              above.
            </p>
          )}
        </Section>

        <Section id="request-lines" title={`Products (${r.lines.length})`}>
          <ul className="flex flex-col gap-3">
            {r.lines.map((l) => {
              const kept = suggestionOf(l.suggestion);
              const overlap = overlaps.get(l.product.id);
              const since =
                kept && r.status === 'requested'
                  ? sinceAskedText(
                      r.id,
                      kept.item,
                      live.data?.items.find((i) => i.product.id === l.product.id),
                    )
                  : null;
              return (
                <li
                  key={l.id}
                  data-line={l.product.id}
                  className="flex flex-col gap-3 rounded-md border border-border bg-surface p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <span className="flex flex-col leading-tight">
                      <span className="font-medium text-fg">{l.product.name}</span>
                      <span className="text-sm text-muted">
                        {packsText(l.qty_packs, l.supplier_item.pack_uom)} asked ·{' '}
                        <BaseQty value={l.base_qty} unit={l.product.base_uom} />
                        {l.supplier_item.supplier_code && (
                          <>
                            {' '}
                            · their code{' '}
                            <span className="whitespace-nowrap">
                              {l.supplier_item.supplier_code}
                            </span>
                          </>
                        )}
                      </span>
                    </span>
                    <LineFigures line={l} />
                  </div>
                  {overlap && <OverlapList name={l.product.name} overlap={overlap} />}
                  {l.note && <p className="text-sm text-fg">“{l.note}”</p>}
                  {kept && (
                    <details className="rounded-md bg-surface-sunken px-3 py-2">
                      <summary className="w-fit cursor-pointer rounded-sm text-sm font-medium text-fg">
                        Why this much: the restock suggestion, as worked out{' '}
                        <AccountDate value={r.requested_at} withTime /> ({kept.history})
                      </summary>
                      {since && (
                        <p
                          className="mt-3 flex items-start gap-2 text-sm text-fg"
                          data-testid="since-asked"
                        >
                          <History aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
                          {since}
                        </p>
                      )}
                      <RestockExplanation item={kept.item} className="mt-3" />
                    </details>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      </div>
    </>
  );
}
